/**
 * Text block layout helpers for product TITLE/DESCRIPTION and generic TEXT sections.
 * Shared by admin editor, preview bundle, and layout compat scripts.
 */

/** @type {Record<string, string>} */
export const TEXT_ALIGN_TO_JUSTIFY = {
  left: "flex-start",
  start: "flex-start",
  center: "center",
  right: "flex-end",
  end: "flex-end",
};

/**
 * @param {string | undefined} textAlign
 * @returns {string | undefined}
 */
export function textAlignToJustifyContent(textAlign) {
  if (!textAlign) return undefined;
  return TEXT_ALIGN_TO_JUSTIFY[String(textAlign).toLowerCase()];
}

/**
 * Maps editor `textAlign` to flex `justifyContent` when blocks use display:flex defaults.
 *
 * @param {Record<string, unknown>} styles
 * @param {{ respectExplicitJustify?: boolean }} [options]
 * @returns {Record<string, unknown>}
 */
export function applyTextAlignToFlexStyles(styles = {}, options = {}) {
  const { respectExplicitJustify = true } = options;
  const next = { ...styles };
  const mapped = textAlignToJustifyContent(
    typeof next.textAlign === "string" ? next.textAlign : undefined
  );
  if (!mapped) return next;

  const explicitJustify =
    respectExplicitJustify &&
    next.justifyContent != null &&
    next.justifyContent !== "" &&
    next.justifyContent !== "center";

  if (!explicitJustify) {
    next.justifyContent = mapped;
  }
  return next;
}

/**
 * @param {unknown} height
 * @param {string} [unit]
 * @returns {string | undefined}
 */
export function formatBlockHeight(height, unit = "px") {
  if (height == null || height === "") return undefined;
  if (typeof height === "number" && Number.isFinite(height)) {
    return `${height}${unit || "px"}`;
  }
  const raw = String(height).trim();
  if (!raw) return undefined;
  if (/px|%|rem|em|vh|vw$/i.test(raw)) return raw;
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return `${numeric}${unit || "px"}`;
  return raw;
}

/**
 * @param {object | undefined} section
 * @param {"desktop" | "mobile"} view
 * @returns {string | undefined}
 */
export function resolveSectionContentHeight(section, view) {
  const custom = section?.setting?.customCSS?.[view] || {};
  const fromCss = formatBlockHeight(custom.height, custom.heightUnit);
  if (fromCss) return fromCss;

  const dim = section?.setting?.dimension?.[view] || {};
  return formatBlockHeight(dim.height, dim.heightUnit);
}

export const DEFAULT_DESCRIPTION_SCROLL_HEIGHT = {
  desktop: "154px",
  mobile: "122px",
};

/** Matches catalog upsell cover image height (desktop 126 / mobile 96). */
export const CATALOG_UPSELL_DESCRIPTION_HEIGHT = {
  desktop: "72px",
  mobile: "56px",
};

/**
 * Scrollable description defaults — only fills gaps; never overwrites merchant height.
 *
 * @param {object | undefined} section
 * @param {"desktop" | "mobile"} view
 * @returns {Record<string, string>}
 */
export function buildDescriptionScrollStyles(section, view, options = {}) {
  const custom = section?.setting?.customCSS?.[view] || {};
  const fallbackHeight =
    options.catalogUpsell === true
      ? CATALOG_UPSELL_DESCRIPTION_HEIGHT[view]
      : DEFAULT_DESCRIPTION_SCROLL_HEIGHT[view];
  const height =
    resolveSectionContentHeight(section, view) || fallbackHeight;

  return {
    display: custom.display || "block",
    height,
    overflowY: custom.overflowY || "auto",
    overflowX: custom.overflowX || "hidden",
    textAlign: custom.textAlign || "left",
    paddingRight: custom.paddingRight ?? (view === "mobile" ? "3px" : "4px"),
  };
}

/** Left-aligned chrome for AI catalog upsell product TITLE / DESCRIPTION / RATING. */
export const CATALOG_UPSELL_TEXT_ALIGN_CSS = {
  textAlign: "left",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  width: "100%",
  display: "block",
};

/**
 * Runtime style patch for catalog-flow product text blocks (preview render).
 *
 * @param {Record<string, unknown>} styles
 * @returns {Record<string, unknown>}
 */
export function applyCatalogUpsellTextAlignStyles(styles = {}) {
  const hasHeight = styles.height != null && styles.height !== "";
  const hidden = styles.display === "none";
  return applyTextAlignToFlexStyles(
    {
      ...styles,
      ...CATALOG_UPSELL_TEXT_ALIGN_CSS,
      display: hidden ? "none" : "block",
      ...(hasHeight ? {} : { height: "auto" }),
    },
    { respectExplicitJustify: false }
  );
}

/**
 * Persist left alignment on product subsections for AI Recommendation Upsell.
 *
 * @param {object | undefined} subsection
 */
export function applyCatalogUpsellProductSubsectionAlign(subsection) {
  if (!subsection?.setting) return;
  const type = subsection.type;
  if (type !== "TITLE" && type !== "DESCRIPTION" && type !== "RATING") return;

  subsection.setting.customCSS = subsection.setting.customCSS || {};

  for (const view of ["desktop", "mobile"]) {
    const existing = subsection.setting.customCSS[view] || {};
    const hidden = existing.display === "none";
    const scroll =
      type === "DESCRIPTION"
        ? buildDescriptionScrollStyles(subsection, view, { catalogUpsell: true })
        : {};

    subsection.setting.customCSS[view] = {
      ...existing,
      ...scroll,
      ...CATALOG_UPSELL_TEXT_ALIGN_CSS,
      ...(type === "DESCRIPTION" && scroll.height ? { height: scroll.height } : {}),
    };

    // Editor eye-toggle uses display:none — layout compat must not force block back on.
    if (hidden) {
      subsection.setting.customCSS[view].display = "none";
    }
  }
}
